import { Alert, AlertDescription } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Loader2 } from "lucide-react";
import { Form, useRefresh, useTranslate } from "ra-core";
import { Link } from "react-router";
import { usePapaParse } from "../misc/usePapaParse";
import type { ContactImportSchema } from "./useContactImport";
import { useContactImport } from "./useContactImport";

import { FormToolbar, FileInput, FileField } from "@/components/admin";
import * as React from "react";
import { useEffect, useState } from "react";
import * as sampleCsv from "./contacts_export.csv?raw";

const SAMPLE_URL = `data:text/csv;name=crm_contacts_sample.csv;charset=utf-8,${encodeURIComponent(
  sampleCsv.default,
)}`;

type ContactImportModalProps = {
  open: boolean;
  onClose(): void;
};

export function ContactImportDialog({
  open,
  onClose,
}: ContactImportModalProps) {
  const refresh = useRefresh();
  const translate = useTranslate();
  const processBatch = useContactImport();
  const { importer, parseCsv, reset } = usePapaParse<ContactImportSchema>({
    batchSize: 10,
    processBatch,
  });

  const [file, setFile] = useState<File | null>(null);

  useEffect(() => {
    if (importer.state === "complete") {
      refresh();
    }
  }, [importer.state, refresh]);

  const handleFileChange = (file: File | null) => {
    setFile(file);
  };

  const startImport = () => {
    if (!file) return;
    parseCsv(file);
  };

  const handleClose = () => {
    reset();
    onClose();
  };

  const handleReset = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();
    reset();
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="max-w-2xl">
        <Form className="flex flex-col gap-4">
          <DialogHeader>
            <DialogTitle>{translate("ra.action.import", { _: "Importar" })}</DialogTitle>
          </DialogHeader>

          <div className="flex flex-col space-y-2">
            {importer.state === "running" && (
              <div className="flex flex-col gap-2">
                <Alert>
                  <AlertDescription className="flex flex-row gap-4">
                    <Loader2 className="h-5 w-5 animate-spin" />
                    {translate("simplecrm.contacts.import.running", { _: "A importação está em execução, não feche esta aba." })}
                  </AlertDescription>
                </Alert>

                <div className="text-sm">
                  {translate("simplecrm.contacts.import.imported", { 
                    _: "Importados {count} de {total} contatos, com {errors} erros.",
                    count: importer.importCount,
                    total: importer.rowCount,
                    errors: importer.errorCount
                  })}
                  {importer.remainingTime !== null && (
                    <>
                      {" "}
                      {translate("simplecrm.contacts.import.estimated_time", { 
                        _: "Tempo estimado restante: {time}.",
                        time: millisecondsToTime(importer.remainingTime)
                      })}{" "}
                      <button
                        onClick={handleReset}
                        className="text-red-600 underline hover:text-red-800"
                      >
                        {translate("simplecrm.contacts.import.stop", { _: "Parar importação" })}
                      </button>
                    </>
                  )}
                </div>
              </div>
            )}

            {importer.state === "error" && (
              <Alert variant="destructive">
                <AlertDescription>
                  {translate("simplecrm.contacts.import.error", { 
                    _: "Falha ao importar este arquivo, certifique-se de que forneceu um arquivo CSV válido." 
                  })}
                </AlertDescription>
              </Alert>
            )}

            {importer.state === "complete" && (
              <Alert>
                <AlertDescription>
                  {translate("simplecrm.contacts.import.complete", { 
                    _: "Importação de contatos concluída. Importados {count} contatos, com {errors} erros",
                    count: importer.importCount,
                    errors: importer.errorCount
                  })}
                </AlertDescription>
              </Alert>
            )}

            {importer.state === "idle" && (
              <>
                <Alert>
                  <AlertDescription className="flex flex-col gap-4">
                    {translate("simplecrm.contacts.import.sample_description", { 
                      _: "Aqui está um arquivo CSV de exemplo que você pode usar como modelo" 
                    })}
                    <Button asChild variant="outline" size="sm">
                      <Link
                        to={SAMPLE_URL}
                        download={"crm_contacts_sample.csv"}
                      >
                        {translate("simplecrm.contacts.import.download_sample", { _: "Baixar exemplo CSV" })}
                      </Link>
                    </Button>{" "}
                  </AlertDescription>
                </Alert>

                <FileInput
                  source="csv"
                  label={translate("simplecrm.contacts.import.csv_file", { _: "Arquivo CSV" })}
                  accept={{ "text/csv": [".csv"] }}
                  onChange={handleFileChange}
                >
                  <FileField source="src" title="title" target="_blank" />
                </FileInput>
              </>
            )}
          </div>
        </Form>

        <div className="flex justify-start pt-6">
          <FormToolbar>
            {importer.state === "idle" ? (
              <Button onClick={startImport} disabled={!file}>
                {translate("ra.action.import", { _: "Importar" })}
              </Button>
            ) : (
              <Button
                variant="outline"
                onClick={handleClose}
                disabled={importer.state === "running"}
              >
                {translate("ra.action.close", { _: "Fechar" })}
              </Button>
            )}
          </FormToolbar>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function millisecondsToTime(ms: number) {
  const seconds = Math.floor((ms / 1000) % 60);
  const minutes = Math.floor((ms / (60 * 1000)) % 60);

  return `${minutes}m ${seconds}s`;
}
